<script lang="tsx">
  import { type Component, computed, defineComponent, h } from 'vue';
  import { useRoute } from 'vue-router';
  import type { FormTableAction } from '@mts/components/Table';
  import { omit, useConfirm } from '@mts-fe/utils';
  import { MButton, PopConfirmButton } from '@mts/components/Button';
  import { Dropdown, DropItem } from '@mts/components/Dropdown';
  import { AsyncExport } from '@mts/components/Export';
  import { t } from '@mts/hooks/web/useI18n';
  import { usePermission } from '@mts/hooks/web/usePermission';
  import { warn } from '@mts/utils';
  import { getGlobalStore, getTableSettingStore } from '@/store';
  import type { ActionBtnItem, ActionHandlerFn, PageMode } from '..';
  import { ApprovalFlowBtn } from '../components/Approval';
  import { AsyncImportBtn, ModalImportBtn, SyncImportBtn } from '../components/Import';
  import { getTableCacheKey, IGNORE_BTN_PROPS } from '..';

  /** 顶部按钮组 */
  const headerBtnCompMap = new Map<string, Component>();
  headerBtnCompMap.set('MButton', MButton);
  headerBtnCompMap.set('PopConfirmButton', PopConfirmButton);
  headerBtnCompMap.set('ApprovalFlowBtn', ApprovalFlowBtn);
  headerBtnCompMap.set('SyncImportBtn', SyncImportBtn);
  headerBtnCompMap.set('ModalImportBtn', ModalImportBtn);
  headerBtnCompMap.set('AsyncImportBtn', AsyncImportBtn);
  headerBtnCompMap.set('AsyncExport', AsyncExport);

  const COMP_MAP: Recordable<{ component: string } & Partial<ActionBtnItem>> = {
    BUTTON: { component: 'MButton' },
    ROW_ADD: { component: 'MButton', type: 'primary' },
    ROW_SAVE: { component: 'MButton', type: 'primary' },
    ROW_BATCH: { component: 'MButton' },
    APPROVAL_SUBMIT: { component: 'MButton' },
    APPROVAL_FLOW: { component: 'ApprovalFlowBtn' },
    APPROVAL_RECORD: { component: 'MButton' },
    IMPORT_FILE: { component: 'SyncImportBtn' },
    IMPORT_MODAL: { component: 'ModalImportBtn' },
    IMPORT_ASYNC: { component: 'AsyncImportBtn' },
    BASE_EXPORT: { component: 'MButton' },
    ASYNC_RECORD: { component: 'MButton' },
    /** @deprecated */
    EXPORT_FILE: { component: 'MButton' },
    /** @deprecated */
    EXPORT_ASYNC: { component: 'AsyncExport' },
    TEMPLATE_PRINT: { component: 'MButton' },
  };

  const { hasPermission } = usePermission();
  export default defineComponent({
    name: 'HeaderBtns',
    inheritAttrs: false,
    props: {
      schema: {
        type: Array as PropType<ActionBtnItem[]>,
        default: () => [],
      },
      /** @deprecated 直接在schema生成时指定pageMode */
      pageMode: {
        type: String as PropType<PageMode>,
      },
      record: {
        type: Object as PropType<Recordable>,
      },
      tableAction: {
        type: Object as PropType<Partial<FormTableAction>>,
      },
      actionHandler: {
        type: Function as PropType<ActionHandlerFn>,
      },
    },
    setup(props) {
      const route = useRoute();
      const globalStore = getGlobalStore();
      const globalParams = { route, sysConfig: globalStore.getConfig };
      props.pageMode && warn('请直接在getBtnsPropsByCode中指定pageMode');

      const renderItem = (itemProps: ActionBtnItem) => {
        const { component, ...compProps } = COMP_MAP[itemProps.eventType] || {};
        if (!component) {
          warn(`[HeaderBtns] ${itemProps.eventType} is not supported`);
          return null;
        }

        const compTitle = t(itemProps.label ?? '');
        const fnArgs: Parameters<ActionHandlerFn> = [
          { ...itemProps, label: compTitle },
          props.record,
        ];
        if (!['IMPORT_FILE', 'IMPORT_MODAL'].includes(itemProps.eventType)) {
          switch (itemProps.eventType) {
            case 'APPROVAL_SUBMIT':
              fnArgs[0].eventApi ??= '/ims-system/approval/submit';
              fnArgs[1].bizType ??= itemProps.bizType;
              fnArgs[1].bizId ??= fnArgs[1].id;
              break;
            case 'EXPORT_FILE':
              const tableId = getTableCacheKey(props.tableAction?.getProps?.().tableId);
              const userColumnConfigDTOList = getTableSettingStore().getColumns(tableId);
              fnArgs[1] = {
                ...omit(props.tableAction?.getSearchInfo?.(), ['size']),
                templateCode: itemProps.templateCode,
                modelId: itemProps.modelId,
                userColumnConfigDTOList,
              };
              break;
            case 'ROW_BATCH':
              fnArgs[0].checkSelect ??= true;
              fnArgs[1] = props.tableAction?.getSelectRows?.() || [];
              break;
          }
        }

        const hideBtn = itemProps.ifBtnShow
          ? itemProps.hidden ||
            !itemProps.ifBtnShow({ column: itemProps, record: fnArgs[1], ...globalParams })
          : itemProps.hidden;
        Object.assign(compProps, {
          ...omit(itemProps, ['label', 'auth', ...IGNORE_BTN_PROPS]),
          disabled:
            itemProps.dynamicDisabled?.({
              column: itemProps,
              record: fnArgs[1],
              ...globalParams,
            }) ||
            itemProps.disabled ||
            (props.pageMode ? itemProps['disabled' + props.pageMode] : void 0),
          hidden: hideBtn || (props.pageMode ? itemProps['hidden' + props.pageMode] : void 0),
          onClick: ['APPROVAL_FLOW', 'IMPORT_MODAL', 'IMPORT_ASYNC', 'EXPORT_ASYNC'].includes(
            itemProps.eventType,
          )
            ? void 0
            : itemProps.openConfirm
              ? () =>
                  useConfirm({
                    ...itemProps.openConfirm,
                    onOk: props.actionHandler?.bind(null, ...fnArgs),
                  })
              : props.actionHandler?.bind(null, ...fnArgs),
        });

        if (itemProps.eventType === 'APPROVAL_FLOW') {
          Object.assign(compProps, {
            action: itemProps.eventApi,
            actionHandler: props.actionHandler,
            record: { ...fnArgs[1], bizId: fnArgs[1].bizId || fnArgs[1].id },
          });
        }
        if (itemProps.eventType === 'IMPORT_ASYNC') {
          delete fnArgs[0].eventApi;
          Object.assign(compProps, {
            action: itemProps.eventApi,
            template: {
              templateCode: itemProps.templateCode,
              ...itemProps.template,
            },
            params: () => ({
              templateCode: itemProps.templateCode,
              ...itemProps['params'],
            }),
            onOk: props.actionHandler?.bind(null, ...fnArgs),
          });
        }
        if (itemProps.eventType === 'EXPORT_ASYNC') {
          Object.assign(compProps, {
            path: itemProps.eventApi,
            template: {
              templateCode: itemProps.templateCode,
              ...itemProps.template,
            },
            params: () => {
              const tableId = getTableCacheKey(props.tableAction?.getProps?.().tableId);
              const userColumnConfigDTOList = getTableSettingStore().getColumns(tableId);
              return {
                ...omit(props.tableAction?.getSearchInfo?.(), ['size']),
                userColumnConfigDTOList,
                templateCode: itemProps.templateCode,
                ...itemProps['params'],
              };
            },
          });
        }
        if (itemProps.eventType === 'IMPORT_MODAL') {
          delete fnArgs[0].eventApi;
          Object.assign(compProps, {
            action: itemProps.eventApi,
            modelId: itemProps.modelId,
            template: {
              templateCode: itemProps.templateCode,
              ...itemProps.template,
            },
            params: () => ({
              route: { query: route.query },
              modelId: itemProps.modelId,
              templateCode: itemProps.templateCode,
              ...itemProps['params'],
            }),
            onOk: props.actionHandler?.bind(null, ...fnArgs),
          });
        }
        // console.log(component, { ...compProps }, fnArgs, 'compProps', itemProps);

        const Comp = headerBtnCompMap.get(component) as ReturnType<typeof defineComponent>;
        return h(Comp, { ...compProps }, compTitle ? () => compTitle : void 0);
      };

      const getBtnProps = (itemProps: ActionBtnItem) => {
        const title = t(itemProps.label ?? '');
        const fnArgs: Parameters<ActionHandlerFn> = [{ ...itemProps, label: title }, props.record];
        return {
          ...omit(itemProps, ['label', 'auth', ...IGNORE_BTN_PROPS]),
          title,
          disabled:
            itemProps.dynamicDisabled?.({
              column: itemProps,
              record: fnArgs[1],
              ...globalParams,
            }) || itemProps.disabled,
          hidden: itemProps.ifBtnShow
            ? !itemProps.ifBtnShow({ column: itemProps, record: fnArgs[1], ...globalParams })
            : itemProps.hidden,
          onClick: itemProps.openConfirm
            ? () =>
                useConfirm({
                  ...itemProps.openConfirm,
                  onOk: props.actionHandler?.bind(null, ...fnArgs),
                })
            : props.actionHandler?.bind(null, ...fnArgs),
        };
      };

      const renderItemSimple = (itemProps: ActionBtnItem) => {
        const { component, ...compProps } = COMP_MAP[itemProps.eventType] || {};
        if (!component) {
          warn(`[HeaderBtns] ${itemProps.eventType} is not supported`);
          return null;
        }

        const btnProps = getBtnProps(itemProps);
        const Comp = headerBtnCompMap.get(component) as ReturnType<typeof defineComponent>;
        return h(
          Comp,
          { ...compProps, ...btnProps },
          btnProps.title ? () => btnProps.title : void 0,
        );
      };

      const renderDropDown = ({
        flatBtns,
        dropItem,
        dropBtns,
      }: {
        flatBtns: ActionBtnItem[];
        dropItem: ActionBtnItem;
        dropBtns: ActionBtnItem[];
      }) => {
        const children = flatBtns.map(renderItemSimple);
        if (dropItem && dropBtns.length) {
          const options = dropBtns.map((i) => {
            const { title, ...restProps } = getBtnProps(i);
            return { ...restProps, value: restProps.eventKey || title, label: title } as DropItem;
          });
          const { onClick, ...btnProps } = getBtnProps(dropItem);
          children.unshift(
            h(Dropdown, { options, placement: 'top' }, () =>
              h(MButton, btnProps, btnProps.title ? () => btnProps.title : void 0),
            ),
          );
        }
        return children;
      };

      const btns = computed(() => [...props.schema].filter((action) => hasPermission(action.auth)));
      const dropDown = computed(() => {
        const index = btns.value.findIndex((i) => i.eventType === 'DROP_DOWN');
        if (index < 0) return false;

        return {
          flatBtns: btns.value.slice(0, index),
          dropItem: btns.value[index],
          dropBtns: btns.value.slice(index + 1),
        };
      });

      return () =>
        btns.value.length
          ? h(
              'div',
              { class: 'flex items-center gap-x-8px' },
              dropDown.value ? renderDropDown(dropDown.value) : btns.value.map(renderItem),
            )
          : null;
    },
  });
</script>
